import { createContext, useContext, useState, useEffect, useCallback, useMemo, ReactNode } from 'react';

export type ExperienceMode = 'classic' | 'alternate';

interface ExperienceModeState {
  mode: ExperienceMode;
  isDiscovered: boolean;
  alternateVisits: number;
  lastModeChange: number;
  discoverySources: string[];
}

interface ExperienceModeContextType {
  mode: ExperienceMode;
  isDiscovered: boolean;
  isAlternate: boolean;
  alternateVisits: number;
  switchToMode: (mode: ExperienceMode) => void;
  discoverAlternateMode: (source: string) => void;
  getModeIntensity: () => number;
}

const STORAGE_KEY = 'human-rooms-experience-mode';

const getDefaultState = (): ExperienceModeState => ({
  mode: 'classic',
  isDiscovered: false,
  alternateVisits: 0,
  lastModeChange: Date.now(),
  discoverySources: [],
});

const loadState = (): ExperienceModeState => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      return { ...getDefaultState(), ...JSON.parse(stored) };
    }
  } catch {}
  return getDefaultState();
};

const saveState = (state: ExperienceModeState) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {}
};

const ExperienceModeContext = createContext<ExperienceModeContextType | undefined>(undefined);

export const ExperienceModeProvider = ({ children }: { children: ReactNode }) => {
  const [state, setState] = useState<ExperienceModeState>(loadState);

  // Apply mode to document
  useEffect(() => {
    const root = document.documentElement;
    root.classList.remove('mode-classic', 'mode-alternate');
    root.classList.add(`mode-${state.mode}`);
    
    // Set CSS custom property for mode intensity
    const intensity = state.alternateVisits > 0 ? Math.min(1, state.alternateVisits / 10) : 0;
    root.style.setProperty('--mode-intensity', intensity.toString());
    
    saveState(state);
  }, [state]);

  const switchToMode = useCallback((mode: ExperienceMode) => {
    setState(prev => ({
      ...prev,
      mode,
      lastModeChange: Date.now(),
      alternateVisits: mode === 'alternate' ? prev.alternateVisits + 1 : prev.alternateVisits,
    }));
  }, []);

  const discoverAlternateMode = useCallback((source: string) => {
    setState(prev => {
      if (prev.isDiscovered && prev.discoverySources.includes(source)) {
        return prev;
      }
      return {
        ...prev,
        isDiscovered: true,
        mode: 'alternate',
        discoverySources: [...new Set([...prev.discoverySources, source])],
        alternateVisits: prev.alternateVisits + 1,
        lastModeChange: Date.now(),
      };
    });
  }, []);

  const getModeIntensity = useCallback(() => {
    if (state.mode === 'classic') return 0;
    return Math.min(1, state.alternateVisits / 10);
  }, [state.mode, state.alternateVisits]);

  const value = useMemo(() => ({
    mode: state.mode,
    isDiscovered: state.isDiscovered,
    isAlternate: state.mode === 'alternate',
    alternateVisits: state.alternateVisits,
    switchToMode,
    discoverAlternateMode,
    getModeIntensity,
  }), [state, switchToMode, discoverAlternateMode, getModeIntensity]);

  return (
    <ExperienceModeContext.Provider value={value}>
      {children}
    </ExperienceModeContext.Provider>
  );
};

export const useExperienceMode = () => {
  const context = useContext(ExperienceModeContext);
  if (!context) {
    throw new Error('useExperienceMode must be used within ExperienceModeProvider');
  }
  return context;
};

export default ExperienceModeContext;
